import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { useCart, SHIPPING_CARRIERS } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, CreditCard, Loader2, Lock, ShieldCheck, Truck, Clock, MapPin, Package } from 'lucide-react';
import Footer from '../components/Footer';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/sonner';
import InPostPointSelector from '../components/InPostPointSelector';
import { GiftCardInput } from '../components/GiftCardInput';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cart, getCartTotal, getShippingCost, coupon, clearCart, selectedCarrier, getSelectedCarrier } = useCart();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState('stripe');
  const [paypalClientId, setPaypalClientId] = useState(null);
  const [configLoaded, setConfigLoaded] = useState(false);

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    province: '',
    country: 'España',
    notes: ''
  });

  // InPost point selection
  const [showInPostSelector, setShowInPostSelector] = useState(false);
  const [selectedInPostPoint, setSelectedInPostPoint] = useState(null);
  const isInPost = selectedCarrier === 'inpost';

  const subtotal = getCartTotal();
  const shippingCost = getShippingCost();
  const discountAmount = coupon ? coupon.discount_amount : 0;
  const [giftCard, setGiftCard] = useState(null);
  const baseTotal = subtotal + shippingCost - discountAmount;
  const giftCardAmount = giftCard ? Math.max(0, Math.min(giftCard.balance, baseTotal - (selectedPayment === 'paypal' ? 1 : 0.5))) : 0;
  const total = baseTotal - giftCardAmount;
  const carrier = getSelectedCarrier();
  const isFreeShipping = subtotal >= 80 || (coupon && coupon.discount_type === 'free_shipping');

  // Load PayPal config
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const response = await axios.get(`${API}/payments/config`);
        if (response.data.paypal_client_id) {
          setPaypalClientId(response.data.paypal_client_id);
        }
        setConfigLoaded(true);
      } catch (error) {
        console.error('Error loading payment config:', error);
        setConfigLoaded(true);
      }
    };
    loadConfig();
  }, []);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!formData.full_name || !formData.email || !formData.phone || !formData.address || 
        !formData.city || !formData.postal_code || !formData.province) {
      toast({
        title: 'Error',
        description: 'Por favor completa todos los campos requeridos',
        variant: 'destructive'
      });
      return false;
    }
    
    // Check InPost point selection
    if (isInPost && !selectedInPostPoint) {
      toast({
        title: 'Punto InPost requerido',
        description: 'Por favor selecciona un punto de recogida InPost',
        variant: 'destructive'
      });
      return false;
    }
    
    return true;
  };

  const handlePayWithStripe = async () => {
    if (!validateForm()) return;

    setLoading(true);

    try {
      const checkoutData = {
        origin_url: window.location.origin,
        cart_items: cart.map(item => ({
          garment_id: item.garment_id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          size: item.size,
          color: item.color
        })),
        shipping_cost: shippingCost,
        shipping_carrier: {
          id: carrier.id,
          name: carrier.name,
          carrier: carrier.carrier,
          service_name: carrier.serviceName,
          packlink_service_id: carrier.packlinkServiceId,
          source: carrier.source,
          delivery_time: carrier.deliveryTime,
          pickup_point: isInPost && selectedInPostPoint ? {
            id: selectedInPostPoint.id,
            name: selectedInPostPoint.name,
            address: selectedInPostPoint.address,
            city: selectedInPostPoint.city,
            postal_code: selectedInPostPoint.postal_code,
            hours: selectedInPostPoint.hours
          } : null
        },
        discount_amount: discountAmount,
        coupon_code: coupon?.code || null,
        affiliate_code: coupon?.affiliate_code || null,
        gift_card_code: giftCard?.code || null,
        customer_info: {
          name: formData.full_name,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          postal_code: formData.postal_code,
          province: formData.province,
          country: formData.country,
          notes: formData.notes
        }
      };

      const response = await axios.post(`${API}/payments/create-checkout`, checkoutData);
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No se recibió URL de pago');
      }

    } catch (error) {
      console.error('Error creating checkout:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo procesar el pago. Intenta de nuevo.',
        variant: 'destructive'
      });
      setLoading(false);
    }
  };

  const handlePayment = () => {
    if (selectedPayment === 'stripe') {
      handlePayWithStripe();
    }
    // PayPal is handled by PayPalButtons component
  };

  if (cart.length === 0) {
    return <CheckoutEmptyRedirect />;
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="flex-1">
      
      <div className="container mx-auto px-4 py-8">
        <Button
          onClick={() => navigate('/cart')}
          variant="ghost"
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver al carrito
        </Button>

        <h1 className="text-3xl font-bold mb-8">Finalizar compra</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Checkout form */}
          <div className="lg:col-span-2">
            <div className="space-y-6">
              {/* Datos personales */}
              <Card className="p-6">
                <h2 className="text-xl font-bold mb-4">Datos personales</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="full_name">Nombre completo *</Label>
                    <Input
                      id="full_name"
                      value={formData.full_name}
                      onChange={(e) => handleChange('full_name', e.target.value)}
                      required
                      data-testid="input-full-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      required
                      data-testid="input-email"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="phone">Teléfono *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      inputMode="tel"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value.replace(/[^\d+\s]/g, ''))}
                      required
                      data-testid="input-phone"
                    />
                  </div>
                </div>
              </Card>

              {/* Dirección de envío */}
              <Card className="p-6">
                <h2 className="text-xl font-bold mb-4">Dirección de envío</h2>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="address">Dirección *</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => handleChange('address', e.target.value)}
                      placeholder="Calle, número, piso, puerta"
                      required
                      data-testid="input-address"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="city">Ciudad *</Label>
                      <Input
                        id="city"
                        value={formData.city}
                        onChange={(e) => handleChange('city', e.target.value)}
                        required
                        data-testid="input-city"
                      />
                    </div>
                    <div>
                      <Label htmlFor="postal_code">Código Postal *</Label>
                      <Input
                        id="postal_code"
                        value={formData.postal_code}
                        onChange={(e) => handleChange('postal_code', e.target.value)}
                        required
                        data-testid="input-postal-code"
                      />
                    </div>
                    <div>
                      <Label htmlFor="province">Provincia *</Label>
                      <Input
                        id="province"
                        value={formData.province}
                        onChange={(e) => handleChange('province', e.target.value)}
                        required
                        data-testid="input-province"
                      />
                    </div>
                  </div>
                </div>
              </Card>

              {/* InPost Point Selection */}
              {isInPost && (
                <Card className="p-6 border-2 border-yellow-300 bg-yellow-50">
                  <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <Package className="w-5 h-5 text-yellow-600" />
                    Punto de recogida InPost
                  </h2>
                  
                  {selectedInPostPoint ? (
                    <div className="space-y-3">
                      <div className="bg-white rounded-lg p-4 border border-yellow-200">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-semibold text-gray-800">{selectedInPostPoint.name}</p>
                            <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                              <MapPin className="w-3 h-3" />
                              {selectedInPostPoint.address}, {selectedInPostPoint.postal_code} {selectedInPostPoint.city}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              <Clock className="w-3 h-3 inline mr-1" />
                              Horario: {selectedInPostPoint.hours}
                            </p>
                          </div>
                          <span className="text-green-600 text-sm font-medium">✓ Seleccionado</span>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowInPostSelector(true)}
                        className="w-full"
                        data-testid="change-inpost-point"
                      >
                        Cambiar punto de recogida
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <MapPin className="w-12 h-12 mx-auto mb-3 text-yellow-500 opacity-50" />
                      <p className="text-gray-600 mb-4">Selecciona el punto InPost donde quieres recoger tu pedido</p>
                      <Button
                        type="button"
                        onClick={() => setShowInPostSelector(true)}
                        className="bg-yellow-500 hover:bg-yellow-600 text-white"
                        data-testid="select-inpost-point"
                      >
                        <MapPin className="w-4 h-4 mr-2" />
                        Seleccionar punto de recogida
                      </Button>
                    </div>
                  )}
                </Card>
              )}

              {/* Notas adicionales */}
              <Card className="p-6">
                <h2 className="text-xl font-bold mb-4">Notas adicionales (opcional)</h2>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  placeholder="Instrucciones especiales de entrega, comentarios..."
                  rows={3}
                  data-testid="input-notes"
                />
              </Card>

              {/* Método de pago */}
              <Card className="p-6">
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Método de pago
                </h2>
                
                <div className="space-y-3">
                  {/* Stripe */}
                  <div
                    onClick={() => setSelectedPayment('stripe')}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      selectedPayment === 'stripe'
                        ? 'border-black bg-neutral-50'
                        : 'border-gray-200 hover:border-neutral-300'
                    }`}
                    data-testid="payment-stripe"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          selectedPayment === 'stripe' ? 'border-black' : 'border-gray-300'
                        }`}>
                          {selectedPayment === 'stripe' && (
                            <div className="w-3 h-3 rounded-full bg-black" />
                          )}
                        </div>
                        <div>
                          <p className="font-semibold">💳 Tarjeta de crédito/débito</p>
                          <p className="text-sm text-gray-500">Visa, Mastercard, American Express, Apple Pay</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1 items-center justify-end max-w-[45%] sm:max-w-none">
                        <img src="/logos/visa.svg" alt="Visa" className="h-6" />
                        <img src="/logos/mastercard.svg" alt="Mastercard" className="h-6" />
                        <svg viewBox="0 0 640 480" className="h-6 w-10 shrink-0" preserveAspectRatio="xMidYMid meet" aria-label="Apple Pay">
                          <path d="M116.3 122.7c-9.4 11.2-24.6 19.9-39.7 18.7-1.9-15.2 5.5-31.4 14.2-41.4 9.4-11.5 25.9-19.6 39.2-20.1 1.6 15.5-4.6 30.8-13.7 42.8zm13.5 21.8c-21.9-1.3-40.6 12.5-51 12.5-10.6 0-26.5-11.8-43.8-11.5-22.5.3-43.4 13.1-55 33.3-23.5 40.7-6 101 16.8 134.1 11.2 16.2 24.5 34.3 42.1 33.7 16.8-.7 23.2-10.9 43.5-10.9 20.3 0 26.2 10.9 43.8 10.5 18.2-.3 29.7-16.5 40.8-32.8 12.8-18.8 18-37 18.3-38-.4-.2-35-13.5-35.4-53.6-.3-33.5 27.4-49.6 28.6-50.5-15.6-23-39.9-25.6-48.5-26.2-20.6-1.7-40.7 11.4-52.2 11.4z" fill="#000"/>
                          <path d="M353.5 82.2c38.4 0 65 26.5 65 64.9 0 38.7-27.1 65.2-66.1 65.2h-42.4v67.5h-30.4V82.2zm-43.5 105h35.2c26.7 0 41.9-14.4 41.9-39.8 0-25.4-15.2-39.6-41.8-39.6h-35.3zm119.5 62.8c0-25.3 19.4-40.8 53.7-42.7l39.5-2.3v-11c0-16-10.8-25.6-28.5-25.6-16.8 0-27.8 9-30.3 22.6H435c1.4-29 25.8-50.5 60.3-50.5 35.4 0 57.5 18.8 57.5 49.2v103h-28.1v-24.6h-.7c-8.2 16.6-26.2 27-44.4 27-27.5 0-46.1-16.8-46.1-44.1zm93.2-13.3v-11.6l-35.6 2.2c-17.8 1.2-27.8 9.6-27.8 23.2 0 13.8 10.4 22.9 26.3 22.9 20.7 0 37.1-14.2 37.1-36.7zm63.8 79V288c2.5.7 10.6.9 13.5.9 19.2 0 29.6-8.1 37.8-28.9l3.6-10.4-57.8-167.5h32l42.2 137.4h.6l42.2-137.4h31.1l-60 175.1c-13.7 38.7-29.6 51.1-62.8 51.1-2.9 0-12.7-.5-15.4-1.2z" fill="#000"/>
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* PayPal */}
                  <div
                    onClick={() => setSelectedPayment('paypal')}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      selectedPayment === 'paypal'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                    data-testid="payment-paypal"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          selectedPayment === 'paypal' ? 'border-blue-500' : 'border-gray-300'
                        }`}>
                          {selectedPayment === 'paypal' && (
                            <div className="w-3 h-3 rounded-full bg-blue-500" />
                          )}
                        </div>
                        <div>
                          <p className="font-semibold">🅿️ PayPal</p>
                          <p className="text-sm text-gray-500">Paga con tu cuenta PayPal</p>
                        </div>
                      </div>
                      <img src="/logos/paypal.svg" alt="PayPal" className="h-6" />
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 mt-4 text-sm text-gray-500">
                  <Lock className="w-4 h-4" />
                  <span>Pago seguro con cifrado SSL</span>
                </div>
              </Card>
            </div>
          </div>

          {/* Order summary */}
          <div className="lg:col-span-1">
            <Card className="p-6 sticky top-4">
              <h2 className="text-xl font-bold mb-4">Resumen del pedido</h2>
              
              <div className="space-y-3 mb-6">
                {cart.map((item, index) => (
                  <div key={index} className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      {item.name} ({item.size}) x{item.quantity}
                    </span>
                    <span className="font-semibold">{(item.price * item.quantity).toFixed(2)}€</span>
                  </div>
                ))}

                <div className="border-t pt-3 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-semibold">{subtotal.toFixed(2)}€</span>
                  </div>
                  
                  {/* Shipping carrier info */}
                  <div className="flex justify-between items-start">
                    <div className="text-gray-600">
                      <div className="flex items-center gap-1">
                        <span>{carrier.icon}</span>
                        <span>{carrier.name}</span>
                      </div>
                      <p className="text-xs text-gray-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {carrier.deliveryTime}
                      </p>
                    </div>
                    <span className={isFreeShipping ? 'text-green-600 font-semibold' : 'font-semibold'}>
                      {isFreeShipping ? '¡GRATIS!' : `${shippingCost.toFixed(2)}€`}
                    </span>
                  </div>
                  
                  {coupon && coupon.discount_amount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Descuento ({coupon.code})</span>
                      <span className="font-semibold">-{discountAmount.toFixed(2)}€</span>
                    </div>
                  )}
                  {giftCardAmount > 0 && (
                    <div className="flex justify-between text-green-600" data-testid="gift-card-discount-line">
                      <span>Tarjeta regalo ({giftCard.code})</span>
                      <span className="font-semibold">-{giftCardAmount.toFixed(2)}€</span>
                    </div>
                  )}
                </div>

                <GiftCardInput giftCard={giftCard} onChange={setGiftCard} />

                <div className="border-t pt-3">
                  <div className="flex justify-between text-xl font-bold">
                    <span>Total</span>
                    <span className="text-black">{total.toFixed(2)}€</span>
                  </div>
                </div>
              </div>

              {/* Payment Button - Stripe */}
              {selectedPayment === 'stripe' && (
                <Button
                  onClick={handlePayment}
                  disabled={loading}
                  className="w-full bg-black hover:bg-neutral-800 text-lg py-6"
                  data-testid="pay-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Procesando...
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      Pagar {total.toFixed(2)}€
                    </>
                  )}
                </Button>
              )}

              {/* Payment Button - PayPal */}
              {selectedPayment === 'paypal' && paypalClientId && configLoaded && (
                <PayPalScriptProvider options={{ 
                  clientId: paypalClientId,
                  currency: "EUR"
                }}>
                  <PayPalButtons
                    style={{ layout: "vertical", label: "pay" }}
                    disabled={loading}
                    createOrder={async () => {
                      if (!validateForm()) {
                        throw new Error('Formulario incompleto');
                      }
                      
                      setLoading(true);
                      try {
                        const orderData = {
                          cart_items: cart.map(item => ({
                            garment_id: item.garment_id,
                            name: item.name,
                            price: item.price,
                            quantity: item.quantity,
                            size: item.size,
                            color: item.color
                          })),
                          shipping_cost: shippingCost,
                          discount_amount: discountAmount,
                          coupon_code: coupon?.code || null,
                          affiliate_code: coupon?.affiliate_code || null,
                          gift_card_code: giftCard?.code || null,
                          origin_url: window.location.origin,
                          customer_info: {
                            name: formData.full_name,
                            email: formData.email,
                            phone: formData.phone,
                            address: formData.address,
                            city: formData.city,
                            postal_code: formData.postal_code,
                            province: formData.province,
                            country: formData.country,
                            notes: formData.notes
                          }
                        };

                        const response = await axios.post(`${API}/payments/paypal/create-order`, orderData);
                        return response.data.order_id;
                      } catch (error) {
                        setLoading(false);
                        console.error('Error creating PayPal order:', error);
                        toast({
                          title: 'Error',
                          description: 'No se pudo crear la orden de PayPal',
                          variant: 'destructive'
                        });
                        throw error;
                      }
                    }}
                    onApprove={async (data) => {
                      try {
                        const response = await axios.post(
                          `${API}/payments/paypal/capture/${data.orderID}?payer_id=${data.payerID}`
                        );
                        
                        if (response.data.status === 'success') {
                          clearCart();
                          toast({
                            title: '¡Pago completado!',
                            description: 'Tu pedido ha sido procesado correctamente',
                          });
                          navigate(`/checkout/success?order_id=${response.data.order_id}&method=paypal`);
                        }
                      } catch (error) {
                        console.error('Error capturing PayPal payment:', error);
                        toast({
                          title: 'Error',
                          description: 'No se pudo completar el pago',
                          variant: 'destructive'
                        });
                      } finally {
                        setLoading(false);
                      }
                    }}
                    onCancel={() => {
                      setLoading(false);
                      toast({
                        title: 'Pago cancelado',
                        description: 'Has cancelado el pago con PayPal',
                      });
                    }}
                    onError={(err) => {
                      setLoading(false);
                      console.error('PayPal error:', err);
                      toast({
                        title: 'Error',
                        description: 'Ha ocurrido un error con PayPal',
                        variant: 'destructive'
                      });
                    }}
                  />
                </PayPalScriptProvider>
              )}

              {selectedPayment === 'paypal' && !paypalClientId && configLoaded && (
                <div className="text-center text-gray-500 py-4">
                  PayPal no está disponible en este momento
                </div>
              )}

              <div className="flex items-center justify-center gap-2 mt-4 text-xs text-gray-500">
                <ShieldCheck className="w-4 h-4" />
                <span>Pago 100% seguro</span>
              </div>
            </Card>
          </div>
        </div>
      </div>
      </div>

      {/* InPost Point Selector Modal */}
      {showInPostSelector && (
        <InPostPointSelector
          postalCode={formData.postal_code}
          selectedPoint={selectedInPostPoint}
          onSelectPoint={setSelectedInPostPoint}
          onClose={() => setShowInPostSelector(false)}
        />
      )}

      <Footer />
      <Toaster />
    </div>
  );
};

export default CheckoutPage;

// Small helper component to redirect to /cart safely (inside useEffect)
// Avoids "setState during render" warning when CheckoutPage is opened with an empty cart.
const CheckoutEmptyRedirect = () => {
  const navigate = useNavigate();
  useEffect(() => {
    navigate('/cart', { replace: true });
  }, [navigate]);
  return null;
};
